/** @format */

import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

@Injectable({
	providedIn: 'root'
})
export class RatingService {
	private readonly document = inject(DOCUMENT);
	private readonly platformId = inject(PLATFORM_ID);

	/**
	 * @description
	 * Reference to mutation observer created in addMutationObserver
	 */
	private mutationObserver: MutationObserver;

	/**
	 * @description
	 * Creates an array of star rating icons for a given aggregate rating and scale ("out of")
	 */
	public getStarRatingByAggregateRating(aggregateRating: number, outOf: number): string[] {
		const EMPTY_STAR = 'star-outlined';
		const FULL_STAR = 'star-filled';
		const HALF_STAR = 'star-half';

		if (aggregateRating === 0 || outOf < 0) {
			return [];
		}

		// Populate the ratings array with empty stars for the "out of" number
		const rating = Array(outOf).fill(EMPTY_STAR);

		if (Number.isNaN(aggregateRating) || aggregateRating < 0) {
			return rating;
		}

		// Add full stars for the whole number of stars
		const wholeStarCount = Math.floor(aggregateRating);
		rating.fill(FULL_STAR, 0, wholeStarCount);

		// If the fractional part is >= 0.5, add a half star after any whole stars
		if (Math.round(aggregateRating - wholeStarCount) === 1) {
			rating[wholeStarCount] = HALF_STAR;
		}

		return rating;
	}

	/**
	 * @description
	 * Creates mutation observer for target document and triggers callback when mutations are made
	 * @param targeSelector - Class or Id of target document that method will query select
	 * @param mutationCallBack - Callback method triggered during document mutation and receives a argument of child_nodes
	 */
	public addMutationObserver(
		targeSelector: string,
		mutationCallBack: (child_nodes: NodeListOf<ChildNode>) => void
	) {
		// Only perform client-side
		if (isPlatformBrowser(this.platformId)) {
			// MutationObserver target
			const targetNode = this.document.querySelector(targeSelector);
			// MutationObserver config
			const config = { attributes: true, childList: true, subtree: true };
			if (targetNode !== undefined && targetNode !== null) {
				// Create a new MutationObserver instance
				this.mutationObserver = new MutationObserver((mutations: any[]) => {
					// eslint-disable-next-line @typescript-eslint/prefer-for-of
					for (let i = 0; i < mutations.length; ++i) {
						const child_nodes = targetNode.childNodes;
						mutationCallBack(child_nodes);
					}
				});
				// Start observing
				this.mutationObserver.observe(targetNode, config);
			}
		}
	}

	public removeMutationObserver() {
		if (isPlatformBrowser(this.platformId) && this.mutationObserver) {
			this.mutationObserver.disconnect();
		}
	}
}
